// AdminDashboard.jsx
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FeedbackHistoryModal } from "./FeedbackHistoryModal.jsx";
import { AssignScribeModal } from "./AssignScribeModal.jsx";
import { adminService } from "../../apis/adminService";
import { ToastNotification } from "./ToastNotification.jsx";
import { BulkAssignmentModal } from "./BulkAssignmentModal.jsx";
import { NotificationBadge } from "./NotificationBadge.jsx";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useNotifications } from '../../context/NotificationContext';
import { ContextMenu, useContextMenu, buildNavContextItems } from "../ui/ContextMenu";
import { ChevronDown } from "lucide-react"; // make sure lucide-react is installed

// ==================== Searchable User Select Filter ====================
const UserSelectFilter = ({ value, onChange, options, placeholder = "Select user" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(opt =>
      String(opt.name || opt.email || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const selectedUser = options.find(opt => opt.userId === value) || null;
  const displayValue = selectedUser ? selectedUser.name : (value === "All" ? "All" : placeholder);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex justify-between items-center text-sm"
      >
        <span className="truncate">{displayValue}</span>
        <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" />
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-auto">
          <div className="sticky top-0 bg-white p-2 border-b">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div>
            <div
              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${value === "All" ? "bg-blue-50 text-blue-700" : ""}`}
              onClick={() => {
                onChange("All");
                setIsOpen(false);
                setSearchTerm("");
              }}
            >
              All
            </div>
            {filteredOptions.map(opt => (
              <div
                key={opt.userId}
                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${value === opt.userId ? "bg-blue-50 text-blue-700" : ""}`}
                onClick={() => {
                  onChange(opt.userId);
                  setIsOpen(false);
                  setSearchTerm("");
                }}
              >
                {opt.name}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-4 py-2 text-gray-500 text-sm">No users found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Column Configuration
const DEFAULT_COLUMNS = [
  { id: "patientName", label: "Patient Name", visible: true, sortable: true },
  { id: "sessionId", label: "Session ID", visible: true, sortable: true },
  { id: "soapVersion", label: "SOAP Ver.", visible: true, sortable: true },
  { id: "status", label: "Status", visible: true, sortable: true },
  { id: "assignedTo", label: "Assigned To", visible: true, sortable: true },
  { id: "assignedDate", label: "Assigned Date", visible: true, sortable: true },
  { id: "assignedBy", label: "Assigned By", visible: true, sortable: true },
  { id: "createdBy", label: "Created By", visible: true, sortable: true },
  { id: "createdOn", label: "Created On", visible: true, sortable: true },
  { id: "dueDate", label: "Due Date", visible: true, sortable: true },
  { id: "lastEdited", label: "Last Modified", visible: true, sortable: true },
  { id: "lastModifiedBy", label: "Last Modified By", visible: true, sortable: true },
  { id: "timeRemaining", label: "Time Remaining", visible: true, sortable: true },
  { id: "feedback", label: "Feedback", visible: true, sortable: true },
  { id: "actions", label: "Actions", visible: true, sortable: true },
];

// Filter configuration — maps each column to a filter UI type
const FILTER_CONFIG = [
  {
    id: "status",
    label: "Status",
    type: "select",
    options: ["All Statuses", "AI Generated", "Assigned", "In Progress", "Pending Review", "Finalized"],
    defaultValue: "All Statuses",
  },
  { id: "assignedTo",     label: "Assigned To",          type: "scribe-select", defaultValue: "All" },
  { id: "assignedBy",     label: "Assigned By",          type: "user-select",   defaultValue: "All" },
  { id: "dateFrom",       label: "Last Modified (From)", type: "date",          defaultValue: "" },
  { id: "dateTo",         label: "Last Modified (To)",   type: "date",          defaultValue: "" },
  { id: "patientName",    label: "Patient Name",         type: "text",          defaultValue: "" },
  { id: "sessionId",      label: "Session ID",           type: "text",          defaultValue: "" },
  {
    id: "soapVersion",
    label: "SOAP Version",
    type: "version-select",
    options: ["All", "1", "2", "3", "4", "5", "6"],
    defaultValue: "All",
  },
  { id: "createdBy",      label: "Created By",           type: "user-select",   defaultValue: "All" },
  { id: "createdOnFrom",  label: "Created On (From)",    type: "date",          defaultValue: "" },
  { id: "createdOnTo",    label: "Created On (To)",      type: "date",          defaultValue: "" },
  { id: "dueDateFrom",    label: "Due Date (From)",      type: "date",          defaultValue: "" },
  { id: "dueDateTo",      label: "Due Date (To)",        type: "date",          defaultValue: "" },
  { id: "lastModifiedBy", label: "Last Modified By",     type: "user-select",   defaultValue: "All" },
];

// Filters shown by default (mirrors the original 4 hardcoded filters)
const DEFAULT_VISIBLE_FILTER_IDS = ["status", "assignedTo", "dateFrom", "dateTo"];

// Storage key for persisting dashboard state
const DASHBOARD_STATE_KEY = "admin_dashboard_state";

// Helper function to save state to localStorage
const saveDashboardState = (state) => {
  try {
    localStorage.setItem(DASHBOARD_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Error saving dashboard state:", error);
  }
};

// Helper function to load state from localStorage
const loadDashboardState = () => {
  try {
    const saved = localStorage.getItem(DASHBOARD_STATE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error("Error loading dashboard state:", error);
    return null;
  }
};

// Build default filter values, merging saved state if available
const buildDefaultFilterValues = (saved) => {
  const defaults = {};
  FILTER_CONFIG.forEach(({ id, defaultValue }) => {
    defaults[id] = saved?.filterValues?.[id] ?? defaultValue;
  });
  return defaults;
};

export function AdminDashboard({
  onViewFeedback,
  onViewSession,
  onRequestExtension,
}) {
  // Load saved state on component mount
  const savedState = useRef(loadDashboardState());
  const allActiveSessionsLoadedRef = useRef(false);

  // ── Core data state ─────────────────────────────────────────────────────────
  const [sessions, setSessions] = useState([]);
  const [kpis, setKpis] = useState({
    totalSessions: 0,
    aiGenerated: 0,
    assigned: 0,
    inProgress: 0,
    pendingReview: 0,
    finalized: 0,
  });
  const [assignableUsers, setAssignableUsers] = useState([]);

  // ── Dynamic filter state ────────────────────────────────────────────────────
  const [filterValues, setFilterValues] = useState(() =>
    buildDefaultFilterValues(savedState.current)
  );
  const [visibleFilterIds, setVisibleFilterIds] = useState(
    savedState.current?.visibleFilterIds || DEFAULT_VISIBLE_FILTER_IDS
  );
  const [showFilterPicker, setShowFilterPicker] = useState(false);

  // Convenient aliases so fetch logic stays readable
  const statusFilter     = filterValues.status     ?? "All Statuses";
  const assignedToFilter = filterValues.assignedTo ?? "All";
  const dateFrom         = filterValues.dateFrom   ?? "";
  const dateTo           = filterValues.dateTo     ?? "";

  // ── UI / modal state ────────────────────────────────────────────────────────
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSessionForAssign, setSelectedSessionForAssign] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedSessionForFeedback, setSelectedSessionForFeedback] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(savedState.current?.page || 1);
  const [pageSize, setPageSize] = useState(savedState.current?.pageSize || 10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [allActiveSessions, setAllActiveSessions] = useState([]);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // ── Column state ────────────────────────────────────────────────────────────
  const [columns, setColumns] = useState(
    savedState.current?.columns
      ? DEFAULT_COLUMNS.map((col) => {
          const saved = savedState.current.columns.find((c) => c.id === col.id);
          return saved ? { ...col, visible: saved.visible } : col;
        })
      : DEFAULT_COLUMNS
  );
  const [sortConfig, setSortConfig] = useState(
    savedState.current?.sortConfig || { key: null, direction: "asc" }
  );
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [globalSearch, setGlobalSearch] = useState(savedState.current?.globalSearch || "");

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  // ── ViewButton sub-component ────────────────────────────────────────────────
  function ViewButton({ session, navigate }) {
    const { menuPosition, openMenu, closeMenu } = useContextMenu();

    if (!session?.id) return null;

    const viewUrl = `/scribe?soapNoteId=${session.id}&tab=soap-note&adminView=true&assigned=true`;

    const handleClick = () => {
      navigate(viewUrl, {
        state: {
          fromAdminDashboard: true,
          showQuickAssign: true,
          sessionData: session,
        },
      });
    };

    return (
      <>
        <button
          onClick={handleClick}
          onContextMenu={openMenu}
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
        >
          View
        </button>
        <ContextMenu
          position={menuPosition}
          onClose={closeMenu}
          items={buildNavContextItems(viewUrl)}
        />
      </>
    );
  }

  // ── Persist state to localStorage ──────────────────────────────────────────
  useEffect(() => {
    saveDashboardState({
      page,
      pageSize,
      filterValues,
      visibleFilterIds,
      globalSearch,
      columns,
      sortConfig,
    });
  }, [page, pageSize, filterValues, visibleFilterIds, globalSearch, columns, sortConfig]);

  // Refresh when returning to dashboard from settings
  useEffect(() => {
    if (location.pathname.includes("/scribe/settings")) {
      const tab = new URLSearchParams(location.search).get("tab");
      if (tab === "assign-task") {
        fetchDashboardData();
        fetchAssignableUsers();
      }
    }
  }, [location]);

  // Fetch data when filters / page / sorting change
  useEffect(() => {
    fetchDashboardData();
  }, [page, pageSize, filterValues, globalSearch, sortConfig, assignableUsers]);

  // Fetch assignable users on mount
  useEffect(() => {
    fetchAssignableUsers();
  }, []);
  

  // Fetch all users for dropdowns
  useEffect(() => {
    const fetchAllUsersForFilters = async () => {
      try {
        const [usersRes, creatorsRes] = await Promise.all([
          adminService.getFilterUsers(),
          adminService.getSessionCreators(),
        ]);
        const byId = new Map();
        const addUsers = (list = []) => {
          list.forEach((user) => {
            if (!user?.userId) return;
            const name = String(user.name || user.email || "").trim();
            if (!name || name === "Unknown" || name === "System") return;
            if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(name)) return;
            const prev = byId.get(user.userId);
            byId.set(user.userId, {
              userId: user.userId,
              email: user.email || prev?.email || "",
              name,
              role: user.role ?? prev?.role ?? null,
              roleid: user.roleid ?? prev?.roleid ?? null,
            });
          });
        };
        if (usersRes.success) addUsers(usersRes.data?.users);
        if (creatorsRes.success) addUsers(creatorsRes.data?.users);
        if (usersRes.success || creatorsRes.success) {
          const formattedUsers = Array.from(byId.values());
        const sortedAllUsers = [...formattedUsers].sort((a, b) => a.name.localeCompare(b.name));
        setAllUsers(sortedAllUsers);

          const admins = formattedUsers.filter(user => {
            const roleId = user.roleid;
            const roleName = user.role;
            return (roleId === 2) || roleName === "admin";
          }).map(user => ({
            userId: user.userId,
            name: user.name || user.email || "Unknown",
          }));

           // Sort admins alphabetically by name
        const sortedAdmins = admins.sort((a, b) => a.name.localeCompare(b.name));
        setAdminUsers(sortedAdmins);
        } else {
          console.error(
            'Failed to fetch users for filters:',
            usersRes.message || creatorsRes.message,
          );
        }
      } catch (err) {
        console.error('Error fetching users for filters:', err);
      }
    };

    fetchAllUsersForFilters();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    if (page !== 1) setPage(1);
  }, [filterValues, globalSearch]);

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedSessions([]);

      // Statistics
      const statsResponse = await adminService.getDashboardStatistics();
      if (statsResponse.success) setKpis(statsResponse.data);

      // Build query params
      const params = { page, limit: pageSize };

      if (statusFilter !== "All Statuses") params.status = statusFilter;

      if (assignedToFilter !== "All") {
        if (assignedToFilter === "-") {
          params.assignedTo = "unassigned";
        } else {
          const selectedScribe = assignableUsers.find((u) => u?.name === assignedToFilter);
          params.assignedTo = selectedScribe ? selectedScribe.userId : assignedToFilter;
        }
      }

      if (dateFrom) params.lastModifiedFrom = dateFrom;
      if (dateTo)   params.lastModifiedTo   = dateTo;

      // Extra dynamic filters – only send if not "All"
      if (filterValues.patientName)                                        params.patientName    = filterValues.patientName;
      if (filterValues.sessionId)                                          params.sessionId      = filterValues.sessionId;
      if (filterValues.assignedBy && filterValues.assignedBy !== "All")    params.assignedBy     = filterValues.assignedBy;
      if (filterValues.createdBy && filterValues.createdBy !== "All")      params.createdBy      = filterValues.createdBy;
      if (filterValues.lastModifiedBy && filterValues.lastModifiedBy !== "All") params.lastModifiedBy = filterValues.lastModifiedBy;
      if (filterValues.soapVersion && filterValues.soapVersion !== "All") params.soapVersion    = filterValues.soapVersion;
      if (filterValues.createdOnFrom)                                      params.createdAtFrom  = filterValues.createdOnFrom;
      if (filterValues.createdOnTo)                                        params.createdAtTo    = filterValues.createdOnTo;
      if (filterValues.dueDateFrom)                                        params.dueDateFrom    = filterValues.dueDateFrom;
      if (filterValues.dueDateTo)                                          params.dueDateTo      = filterValues.dueDateTo;
      if (globalSearch)                                                    params.search         = globalSearch;

      // Sorting
      if (sortConfig.key) {
        const sortFieldMap = {
          patientName:  "patientName",
          sessionId:    "sessionId",
          soapVersion:  "soapVersion",
          status:       "status",
          assignedTo:   "assignedTo",
          assignedDate: "assignedAt",
          assignedBy:   "assignedBy",
          createdBy:    "createdBy",
          createdOn:    "createdAt",
          dueDate:      "dueDate",
          lastEdited:   "updatedAt",
          lastModifiedBy: "lastModifiedBy",
        };
        params.sortBy    = sortFieldMap[sortConfig.key] || sortConfig.key;
        params.sortOrder = sortConfig.direction;
      }

      // Fetch SOAP notes
      const notesResponse = await adminService.getDashboardSOAPNotes(params);

      if (notesResponse.success) {
        const soapNotes  = notesResponse.data.soapNotes || [];
        const pagination = notesResponse.data.pagination || {
          total: 0, totalPages: 1, currentPage: 1, limit: pageSize,
        };

        setSessions(soapNotes.map(convertSOAPNoteToSession));
        setTotalPages(pagination.totalPages || 1);
        setTotalItems(pagination.total || 0);

        if (page > pagination.totalPages && pagination.totalPages > 0) setPage(1);
      } else {
        console.error("Failed to fetch SOAP notes:", notesResponse.message);
        setSessions([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard data. Please try again.");
      setSessions([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const response = await adminService.getAssignableUsers();
      if (response.success) {
        const users = response.data?.users || [];
      // Sort users alphabetically by name
      const sortedUsers = [...users].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
      setAssignableUsers(sortedUsers);
      } else {
        console.error("Failed to fetch assignable users:", response.message);
        setAssignableUsers([]);
      }
    } catch (err) {
      console.error("Error fetching assignable users:", err);
      setAssignableUsers([]);
    }
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleRefresh = () => {
    fetchDashboardData();
    fetchAssignableUsers();
  };

  const handleSearch = useCallback((value) => {
    setGlobalSearch(value);
    setPage(1);
  }, []);

  /** Update one filter value and reset to page 1 */
  const handleFilterChange = (filterId, value) => {
    setFilterValues((prev) => ({ ...prev, [filterId]: value }));
    setPage(1);
  };

  /** Toggle a filter's visibility in the picker */
  const handleFilterToggle = (filterId) => {
    setVisibleFilterIds((prev) =>
      prev.includes(filterId)
        ? prev.filter((id) => id !== filterId)
        : [...prev, filterId]
    );
  };

  const handleResetFilters = () => {
    const defaults = {};
    FILTER_CONFIG.forEach(({ id, defaultValue }) => { defaults[id] = defaultValue; });
    setFilterValues(defaults);
    setGlobalSearch("");
    setPage(1);
    setSortConfig({ key: null, direction: "asc" });
  };

  const handleNotificationClick = (notification) => {
    if (notification.relatedEntityType === "soap_note" && notification.relatedEntityId) {
      navigate(
        `/scribe?soapNoteId=${notification.relatedEntityId}&tab=soap-note&forceRefresh=${Date.now()}`,
        { state: { notificationSource: true, forceRefresh: true } }
      );
    }
    setTimeout(() => fetchDashboardData(), 300);
  };

  // ── Data conversion ─────────────────────────────────────────────────────────

  const convertSOAPNoteToSession = (soapNote) => {
    if (!soapNote) return null;

    let patientName = soapNote.patientName || "Unknown Patient";
    if (!patientName && soapNote.patient) {
      if (typeof soapNote.patient === "string") {
        patientName = soapNote.patient;
      } else if (soapNote.patient.firstName || soapNote.patient.lastName) {
        patientName = `${soapNote.patient.firstName || ""} ${soapNote.patient.lastName || ""}`.trim();
      }
    }

    let assignedTo = "-";
    if (soapNote.assignedTo) {
      if (typeof soapNote.assignedTo === "string") {
        assignedTo = soapNote.assignedTo;
      } else if (soapNote.assignedUser) {
        assignedTo =
          soapNote.assignedUser.name ||
          `${soapNote.assignedUser.firstName || ""} ${soapNote.assignedUser.lastName || ""}`.trim();
      }
    }
    // Extract assignedToId
  let assignedToId = null;
  if (soapNote.assignedTo) {
    if (typeof soapNote.assignedTo === 'object' && soapNote.assignedTo.userId) {
      assignedToId = soapNote.assignedTo.userId;
    } else if (soapNote.assignedToId) {
      assignedToId = soapNote.assignedToId;
    }
  }

    const lastModified   = soapNote.lastModified || soapNote.updatedAt || soapNote.createdAt;
    const lastEdited     = lastModified ? new Date(lastModified).toLocaleDateString() : "N/A";
    const assignedDate   = soapNote.assignedAt ? new Date(soapNote.assignedAt).toLocaleDateString() : null;
    const assignedBy     = soapNote.assignedBy || null;
    const lastModifiedBy = soapNote.assignedTo ? assignedTo : null;
    const createdBy      = soapNote.createdBy || soapNote.userId || "System";
    const createdOn      = soapNote.createdAt ? formatDateTime(new Date(soapNote.createdAt)) : "N/A";

    let dueDate = null;
    if (soapNote.assignedAt) {
      try {
        const date = new Date(soapNote.assignedAt);
        date.setDate(date.getDate() + 2);
        dueDate = date.toISOString().split("T")[0];
      } catch (e) {
        console.error("Error calculating due date:", e);
      }
    }

    return {
      id:            soapNote.soapNoteId || "",
      sessionId:     soapNote.sessionId  || "",
      patientId:     soapNote.patient?.patientId || "N/A",
      patientName,
      encounterType: soapNote.session?.sessionName || "N/A",
      soapVersion:   soapNote.soapVersion ? soapNote.soapVersion.replace("v", "") : "1",
      status:        soapNote.status || "Unknown",
      assignedTo,
      assignedToId,
      assignedDate,
      assignedBy,
      createdBy,
      createdOn,
      dueDate,
      lastEdited,
      lastModifiedBy,
      timeRemaining: soapNote.timeRemaining,
      feedbackCount: soapNote.feedbackCount || 0,
      rawData:       soapNote,
    };
  };

  const formatDateTime = (date) => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      }).format(date);
    } catch {
      return "N/A";
    }
  };

  // ── Scribe options ──────────────────────────────────────────────────────────
  const uniqueScribes = [
    ...new Set(
      assignableUsers.map((u) => u?.name).filter((n) => n && n !== "-" && n !== "")
    ),
  ];
  const allScribeOptions = ["-", ...uniqueScribes];

  // ── Selection helpers ───────────────────────────────────────────────────────
  const isSessionSelectable = (session) =>
    session &&
    session.assignedTo === "-" &&
    !session.status.includes("Finalized") &&
    !session.status.includes("Approved") &&
    !session.status.includes("Signed");

  const selectableSessionsCount = sessions.filter(isSessionSelectable).length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedSessions(
        sessions.filter(isSessionSelectable).map((s) => s.id).filter(Boolean)
      );
    } else {
      setSelectedSessions([]);
    }
  };

  const handleSelectSession = (sessionId, checked) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (checked && isSessionSelectable(session)) {
      setSelectedSessions((prev) => [...prev, sessionId]);
    } else {
      setSelectedSessions((prev) => prev.filter((id) => id !== sessionId));
    }
  };

  // ── Assign / re-assign / bulk ───────────────────────────────────────────────
  const handleAssignClick = (session) => {
    if (!session) return;
    setSelectedSessionForAssign(session);
    setShowAssignModal(true);
    if (allActiveSessionsLoadedRef.current) return;
    allActiveSessionsLoadedRef.current = true;
    (async () => {
      try {
        const response = await adminService.getDashboardSOAPNotes({ limit: 5000 });
        if (!response.success) {
          allActiveSessionsLoadedRef.current = false;
          return;
        }
        const notes = response.data.soapNotes || [];
        const active = notes.filter((s) =>
          !s.status?.includes("Finalized") &&
          !s.status?.includes("Approved") &&
          !s.status?.includes("Signed")
        );
        setAllActiveSessions(active.map(convertSOAPNoteToSession));
      } catch (err) {
        allActiveSessionsLoadedRef.current = false;
        console.error("Failed to fetch all active sessions", err);
      }
    })();
  };

  const handleViewFeedbackClick = async (session) => {
    if (!session?.id) return;
    try {
      const response = await adminService.getSOAPNoteFeedback(session.id);
      if (response.success) {
        const feedbacks = response.data?.feedbacks || [];
        setSelectedSessionForFeedback({
          ...session,
          feedbackHistory: feedbacks.map((fb) => ({
            id:        fb.feedbackId,
            scribeName: fb.userName || "Unknown",
            rating:    fb.type === "thumbs_up" ? "positive" : "negative",
            date:      fb.createdAt,
            version:   session.soapVersion,
            comment:   fb.feedbackText || fb.explanation || "",
            section:   fb.section,
          })),
        });
        setShowFeedbackModal(true);
      }
    } catch (err) {
      console.error("Error fetching feedback:", err);
    }
  };

  const handleAssignSession = async (sessionId, assignedToName) => {
    try {
      if (!assignableUsers.length)
        return setToast({ show: true, message: "No assignable users available", type: "error" });
      const user = assignableUsers.find((u) => u?.name === assignedToName);
      if (!user)
        return setToast({ show: true, message: "Selected scribe not found", type: "error" });
      const response = await adminService.assignSOAPNote({ soapNoteId: sessionId, assignedTo: user.userId });
      if (response.success) {
        setToast({ show: true, message: `Successfully assigned to ${assignedToName}`, type: "success" });
        await fetchDashboardData();
      } else {
        setToast({ show: true, message: `Assignment failed: ${response.message}`, type: "error" });
      }
    } catch (err) {
      setToast({ show: true, message: `Error: ${err.message}`, type: "error" });
    }
  };

  const handleReassignSession = async (sessionId, assignedToName) => {
    try {
      if (!assignableUsers.length)
        return setToast({ show: true, message: "No assignable users available", type: "error" });
      const user = assignableUsers.find((u) => u?.name === assignedToName);
      if (!user)
        return setToast({ show: true, message: "Selected scribe not found", type: "error" });
      const response = await adminService.reassignSOAPNote({ soapNoteId: sessionId, assignedTo: user.userId });
      if (response.success) {
        setToast({ show: true, message: `Successfully reassigned to ${assignedToName}`, type: "success" });
        await fetchDashboardData();
      } else {
        setToast({ show: true, message: `Reassignment failed: ${response.message}`, type: "error" });
      }
    } catch (err) {
      setToast({ show: true, message: `Error: ${err.message}`, type: "error" });
    }
  };

  const handleBulkAssign = async (selectedSessionIds, scribeId, scribeName) => {
    try {
      if (!assignableUsers.length)
        return setToast({ show: true, message: "No assignable users available", type: "error" });
      const user = assignableUsers.find((u) => u.userId === scribeId);
      if (!user)
        return setToast({ show: true, message: "Selected scribe not found", type: "error" });
      const response = await adminService.bulkAssignSOAPNotes({
        soapNoteIds: selectedSessionIds.filter(Boolean),
        assignedTo:  user.userId,
      });
      if (response.success) {
        const successCount = response.data?.summary?.successful || selectedSessionIds.length;
        setToast({ show: true, message: `Successfully assigned ${successCount} sessions to ${scribeName}`, type: "success" });
        await fetchDashboardData();
        setSelectedSessions([]);
        setShowBulkAssignModal(false);
      } else {
        setToast({ show: true, message: `Bulk assignment failed: ${response.message}`, type: "error" });
      }
    } catch (err) {
      setToast({ show: true, message: `Error: ${err.message}`, type: "error" });
    }
  };

  // ── Pagination ──────────────────────────────────────────────────────────────
  const handlePageSizeChange = (newSize) => { setPageSize(newSize); setPage(1); };
  const handlePageChange     = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  // ── Column handlers ─────────────────────────────────────────────────────────
  const handleColumnToggle  = (columnId) =>
    setColumns(columns.map((col) =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  const handleResetColumns  = () => setColumns(DEFAULT_COLUMNS);

  // ── Sorting ─────────────────────────────────────────────────────────────────
  const handleSort = (columnId) => {
    const column = columns.find((col) => col.id === columnId);
    if (!column?.sortable) return;
    if (sortConfig.key === columnId && sortConfig.direction === "desc") {
      setSortConfig({ key: null, direction: "asc" });
      return;
    }
    const direction = sortConfig.key === columnId && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key: columnId, direction });
    setPage(1);
  };

  const getSortIcon = (columnId) => {
    if (sortConfig.key !== columnId) return <span className="text-gray-300 text-xs ml-1">⇅</span>;
    return sortConfig.direction === "asc"
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  // ── KPI data ────────────────────────────────────────────────────────────────
  const kpiData = [
    { label: "Total Sessions", value: kpis.totalSessions || 0 },
    { label: "AI Generated",   value: kpis.aiGenerated   || 0 },
    { label: "Assigned",       value: kpis.assigned       || 0 },
    { label: "In Progress",    value: kpis.inProgress     || 0 },
    { label: "Pending Review", value: kpis.pendingReview  || 0 },
    { label: "Finalized",      value: kpis.finalized      || 0 },
  ];

  const getTimeRemaining = (session) => {
    if (!session) return "-";
    if (session.timeRemaining) return session.timeRemaining;
    if (!session.dueDate) return "-";
    try {
      const diff = new Date(session.dueDate) - new Date();
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      if (days < 0)   return `${Math.abs(days)} days overdue`;
      if (days === 0) return "Due today";
      return `${days} days left`;
    } catch {
      return "-";
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return "bg-gray-100 text-gray-800 border border-gray-200 rounded-full";
    switch (status.toLowerCase()) {
      case "ai generated":   return "bg-blue-100   text-blue-800   border border-blue-200   rounded-full";
      case "assigned":       return "bg-yellow-100 text-yellow-800 border border-yellow-200 rounded-full";
      case "in progress":
      case "draft":          return "bg-orange-100 text-orange-800 border border-orange-200 rounded-full";
      case "pending review":
      case "pending_review": return "bg-purple-100 text-purple-800 border border-purple-200 rounded-full";
      case "finalized":
      case "approved":
      case "signed":         return "bg-green-100  text-green-800  border border-green-200  rounded-full";
      default:               return "bg-gray-100   text-gray-800   border border-gray-200   rounded-full";
    }
  };

  const visibleColumns = columns.filter((col) => col.visible);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 w-full">
      {toast.show && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <div className="max-w-full mx-auto">
        {/* ── Header ── */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Assign Tasks to Scribes Dashboard
              </h2>
              <p className="text-gray-600 mt-1">
                Manage and assign medical transcription sessions to scribes
              </p>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBadge
                notifications={notifications}
                notificationCount={unreadCount}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                onClearNotification={deleteNotification}
                onNotificationClick={handleNotificationClick}
              />
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* ── KPI Cards ── */}
        {loading && page === 1 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            {[...Array(6)].map((_, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow p-6 border border-gray-200 animate-pulse"
              >
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            {kpiData.map((kpi, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow p-6 border border-gray-200"
              >
                <div className="text-xs font-medium text-gray-500 mb-2">
                  {kpi.label}
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {kpi.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Action Buttons ── */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => setShowBulkAssignModal(true)}
            disabled={selectedSessions.length === 0}
            className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
              selectedSessions.length === 0
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Bulk Assign ({selectedSessions.length})
          </button>
          <button
            onClick={onViewFeedback}
            className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            View Feedback
          </button>
        </div>

        {/* ── Filters Section ── */}
        <div className="bg-white rounded-xl shadow mb-6 border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Filters & Search
            </h3>

            <div className="flex gap-3 flex-wrap items-center">
              {/* Global Search */}
              <div className="relative w-72">
                <input
                  type="text"
                  placeholder="Search across all columns..."
                  value={globalSearch}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleSearch(globalSearch)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                {globalSearch && (
                  <button
                    onClick={() => handleSearch("")}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* ── Manage Filters picker ── */}
              <div className="relative">
                <button
                  onClick={() => setShowFilterPicker((v) => !v)}
                  className="flex items-center px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-xs hover:bg-gray-50 transition-colors font-medium"
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                    />
                  </svg>
                  Manage Filters
                  {visibleFilterIds.length !==
                    DEFAULT_VISIBLE_FILTER_IDS.length && (
                    <span className="ml-1.5 bg-blue-600 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                      {visibleFilterIds.length}
                    </span>
                  )}
                </button>

                {showFilterPicker && (
                  <div className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                    <div className="p-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
                      <h4 className="font-semibold text-sm text-gray-900">
                        Show / Hide Filters
                      </h4>
                      <button
                        onClick={() =>
                          setVisibleFilterIds(DEFAULT_VISIBLE_FILTER_IDS)
                        }
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Reset
                      </button>
                    </div>
                    <div className="p-3 space-y-1">
                      {FILTER_CONFIG.map((filter) => (
                        <label
                          key={filter.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={visibleFilterIds.includes(filter.id)}
                            onChange={() => handleFilterToggle(filter.id)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-sm text-gray-700">
                            {filter.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleResetFilters}
                className="px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-xs hover:bg-gray-50 transition-colors font-medium"
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* Dynamic filter grid */}
          {visibleFilterIds.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {FILTER_CONFIG.filter((f) => visibleFilterIds.includes(f.id)).map(
                (filter) => (
                  <div key={filter.id} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {filter.label}
                    </label>

                    {filter.type === "select" && (
                      <select
                        value={filterValues[filter.id]}
                        onChange={(e) =>
                          handleFilterChange(filter.id, e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        {filter.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === "user-select" && (
                      <UserSelectFilter
                        value={filterValues[filter.id]}
                        onChange={(value) => handleFilterChange(filter.id, value)}
                        options={filter.id === "assignedBy" ? adminUsers : allUsers}
                        placeholder="Select user"
                      />
                    )}

                    {filter.type === "version-select" && (
                      <select
                        value={filterValues[filter.id]}
                        onChange={(e) =>
                          handleFilterChange(filter.id, e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        {filter.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === "All" ? "All Versions" : `v${opt}`}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === "scribe-select" && (
                      <select
                        value={filterValues[filter.id]}
                        onChange={(e) =>
                          handleFilterChange(filter.id, e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="All">All</option>
                        {allScribeOptions.map((scribe, idx) => (
                          <option key={idx} value={scribe}>
                            {scribe === "-" ? "Unassigned" : scribe}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === "text" && (
                      <div className="relative">
                        <input
                          type="text"
                          value={filterValues[filter.id]}
                          onChange={(e) =>
                            handleFilterChange(filter.id, e.target.value)
                          }
                          placeholder={`Filter by ${filter.label.toLowerCase()}...`}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        {filterValues[filter.id] && (
                          <button
                            onClick={() => handleFilterChange(filter.id, "")}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-xs"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    )}

                    {filter.type === "date" && (
                      <input
                        type="date"
                        value={filterValues[filter.id]}
                        onChange={(e) =>
                          handleFilterChange(filter.id, e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    )}
                  </div>
                ),
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">
              No filters selected — click <strong>Manage Filters</strong> to add
              some.
            </p>
          )}
        </div>

        {/* ── Session Table ── */}
        <div className="bg-white rounded-xl shadow border border-gray-200">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {loading
                  ? "Loading sessions..."
                  : `Sessions (${totalItems} total)`}
              </h3>

              <div className="flex items-center gap-4 flex-wrap">
                {selectedSessions.length > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedSessions.length} selected
                  </span>
                )}

                {/* Column Picker */}
                <div className="relative">
                  <button
                    onClick={() => setShowColumnPicker(!showColumnPicker)}
                    className="flex items-center px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-xs hover:bg-gray-50 transition-colors font-medium"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z"
                      />
                    </svg>
                    Columns
                  </button>

                  {showColumnPicker && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                      <div className="p-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
                        <h4 className="font-semibold text-sm text-gray-900">
                          Show/Hide Columns
                        </h4>
                        <button
                          onClick={handleResetColumns}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Reset
                        </button>
                      </div>
                      <div className="p-3 space-y-2">
                        {columns.map((column) => (
                          <label
                            key={column.id}
                            className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={column.visible}
                              onChange={() => handleColumnToggle(column.id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600"
                            />
                            <span className="text-sm text-gray-700">
                              {column.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Page size selector */}
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>

                {/* Pagination controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="p-3 text-left w-12">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={
                          sessions.length > 0 &&
                          selectableSessionsCount > 0 &&
                          selectedSessions.length === selectableSessionsCount
                        }
                        onChange={handleSelectAll}
                        disabled={loading || selectableSessionsCount === 0}
                        title={
                          selectableSessionsCount === 0
                            ? "No unassigned tasks available"
                            : `Select all ${selectableSessionsCount} unassigned tasks`
                        }
                      />
                    </th>
                    {visibleColumns.map((column) => (
                      <th
                        key={column.id}
                        onClick={() => column.sortable && handleSort(column.id)}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 ${
                          column.sortable
                            ? "cursor-pointer hover:bg-gray-100"
                            : ""
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{column.label}</span>
                          {column.sortable && getSortIcon(column.id)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(10)].map((_, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        {[...Array(visibleColumns.length + 1)].map(
                          (_, colIdx) => (
                            <td key={colIdx} className="p-3">
                              <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                            </td>
                          ),
                        )}
                      </tr>
                    ))
                  ) : sessions.length > 0 ? (
                    sessions.map((session) => {
                      const isSelectable = isSessionSelectable(session);
                      return (
                        <tr
                          key={session.id}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="p-3">
                            <input
                              type="checkbox"
                              className={`w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                                !isSelectable
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              checked={selectedSessions.includes(session.id)}
                              onChange={(e) =>
                                handleSelectSession(
                                  session.id,
                                  e.target.checked,
                                )
                              }
                              disabled={!isSelectable || loading}
                              title={
                                !isSelectable
                                  ? session.assignedTo !== "-"
                                    ? "Already assigned"
                                    : "Cannot select finalized/approved sessions"
                                  : "Select for bulk assignment"
                              }
                            />
                          </td>

                          {visibleColumns.map((column) => (
                            <td key={column.id} className="p-3 text-sm">
                              {column.id === "patientName" && (
                                <div className="font-medium text-gray-900">
                                  {session.patientName}
                                </div>
                              )}
                              {column.id === "sessionId" && (
                                <div className="font-mono text-gray-700">
                                  {session.sessionId}
                                </div>
                              )}
                              {column.id === "soapVersion" && (
                                <span className="inline-block border border-gray-300 px-2 py-1 text-xs font-mono bg-gray-50 rounded">
                                  v{session.soapVersion}
                                </span>
                              )}
                              {column.id === "status" && (
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium ${getStatusBadge(session.status)}`}
                                >
                                  {session.status}
                                </span>
                              )}
                              {column.id === "createdBy" && (
                                <div className="text-gray-700 font-medium">
                                  {session.createdBy || "-"}
                                </div>
                              )}
                              {column.id === "createdOn" && (
                                <div className="text-gray-700">
                                  {session.createdOn}
                                </div>
                              )}
                              {column.id === "assignedTo" && (
                                <div className="text-gray-700">
                                  {session.assignedTo === "-"
                                    ? "Unassigned"
                                    : session.assignedTo}
                                </div>
                              )}
                              {column.id === "assignedDate" && (
                                <div className="text-gray-700">
                                  {session.assignedDate || "-"}
                                </div>
                              )}
                              {column.id === "assignedBy" && (
                                <div className="text-gray-700">
                                  {session.assignedBy || "-"}
                                </div>
                              )}
                              {column.id === "dueDate" && (
                                <div className="text-gray-700">
                                  {session.dueDate || "-"}
                                </div>
                              )}
                              {column.id === "lastEdited" && (
                                <div className="text-gray-600">
                                  {session.lastEdited}
                                </div>
                              )}
                              {column.id === "lastModifiedBy" && (
                                <div className="text-gray-700">
                                  {session.lastModifiedBy || "-"}
                                </div>
                              )}
                              {column.id === "timeRemaining" && (
                                <div
                                  className={
                                    session.dueDate &&
                                    new Date(session.dueDate) < new Date()
                                      ? "text-red-600 font-medium"
                                      : "text-gray-700"
                                  }
                                >
                                  {getTimeRemaining(session)}
                                </div>
                              )}
                              {column.id === "feedback" &&
                                (session.feedbackCount > 0 ? (
                                  <button
                                    onClick={() =>
                                      handleViewFeedbackClick(session)
                                    }
                                    className="text-xs px-3 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
                                  >
                                    {session.feedbackCount}{" "}
                                    {session.feedbackCount === 1
                                      ? "entry"
                                      : "entries"}
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-400">
                                    No feedback
                                  </span>
                                ))}
                              {column.id === "actions" && (
                                <div className="flex flex-col gap-2">
                                  <ViewButton
                                    session={session}
                                    navigate={navigate}
                                  />
                                  {session.status !== "Finalized" &&
                                    session.status !== "Approved" &&
                                    session.status !== "Signed" && (
                                      <button
                                        onClick={() =>
                                          handleAssignClick(session)
                                        }
                                        className={`text-sm text-left ${
                                          session.assignedTo === "-"
                                            ? "text-green-600 hover:text-green-800"
                                            : "text-amber-600 hover:text-amber-800"
                                        } hover:underline transition-colors`}
                                      >
                                        {session.assignedTo === "-"
                                          ? "Assign"
                                          : "Re-assign"}
                                      </button>
                                    )}
                                </div>
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={visibleColumns.length + 1}
                        className="p-8 text-center"
                      >
                        <div className="text-gray-500 mb-2">
                          No sessions found
                        </div>
                        <button
                          onClick={handleResetFilters}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        >
                          Reset filters to show all sessions
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Assign / Re-assign Modal ── */}
      {showAssignModal && selectedSessionForAssign && (
        <AssignScribeModal
          session={selectedSessionForAssign}
          assignableUsers={assignableUsers}
          sessions={allActiveSessions}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedSessionForAssign(null);
          }}
          onAssign={handleAssignSession}
          onReassign={handleReassignSession}
          onRequestExtension={onRequestExtension}
        />
      )}

      {/* ── Bulk Assignment Modal ── */}
      {showBulkAssignModal && (
        <BulkAssignmentModal
          sessions={sessions}
          selectedSessions={selectedSessions}
          selectedCount={selectedSessions.length}
          assignableUsers={assignableUsers}
          onClose={() => setShowBulkAssignModal(false)}
          onAssign={handleBulkAssign}
        />
      )}

      {/* ── Feedback History Modal ── */}
      {showFeedbackModal && selectedSessionForFeedback && (
        <FeedbackHistoryModal
          session={selectedSessionForFeedback}
          onClose={() => {
            setShowFeedbackModal(false);
            setSelectedSessionForFeedback(null);
            fetchDashboardData();
          }}
        />
      )}
    </div>
  );
}